import React, { useRef, useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import type { DG } from '@cotera/sdk/core';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  readonlyEditorExtensions,
  type MentionClickData,
} from './readonly-editor-extensions';

// Re-exported from its original module path so existing importers keep working.
export type { MentionClickData };

type ReadonlyEditorStateProps = {
  editorState: DG.DocumentEditor.DocumentState;
  className?: string;
  /** When provided, mention chips are clickable and invoke this handler */
  onMentionClick?: (data: MentionClickData) => void;
  /** Show link indicator + tooltip for mentions owned by another user. */
  showMentionOwnershipIndicator?: boolean;
  mentionOwnershipTooltipContent?: string;
};

/**
 * Renders a TipTap document state in readonly mode.
 * Used for displaying rich user messages in chat.
 */
export const ReadonlyEditorState: React.FC<ReadonlyEditorStateProps> = ({
  editorState,
  className,
  onMentionClick,
  showMentionOwnershipIndicator = false,
  mentionOwnershipTooltipContent,
}) => {
  const onMentionClickRef = useRef(onMentionClick);
  useEffect(() => {
    onMentionClickRef.current = onMentionClick;
  }, [onMentionClick]);

  const editor = useEditor({
    extensions: readonlyEditorExtensions({
      onMentionClick: (data) => {
        onMentionClickRef.current?.(data);
      },
      showMentionOwnershipIndicator,
      mentionOwnershipTooltipContent,
    }),
    content: editorState,
    editable: false,
    editorProps: {
      attributes: {
        // `prose-ignore` opts out of the global document paragraph rule in
        // styles.css (`.tiptap.ProseMirror p:not(:first-child)`), which forces
        // 1rem / margin-top 20px onto every paragraph after the first. That doc
        // rule makes a sent multi-paragraph message shrink its later lines to
        // 13px with a big gap while the first line stays 16px. Opting out lets
        // the `[&_p]` styles below apply uniformly so the message matches the
        // composer. Mirrors the same fix in `chat-tiptap-input.tsx`.
        class:
          'prose-ignore prose prose-sm dark:prose-invert max-w-none [&_p]:text-base [&_p]:my-0.5 [&_p:first]:mt-0 [&_p:last]:mb-0 [&_h1]:my-1 [&_h2]:my-1 [&_h3]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_blockquote]:my-1 [&_pre]:my-1',
      },
    },
  });

  if (editor === undefined || editor === null) {
    return null;
  }

  return (
    <div className={cn('min-w-0', className)}>
      <EditorContent editor={editor} />
    </div>
  );
};
